// src/pages/userprofile/WebhooksPage.tsx
// Webhook endpoints management inside user Settings — list, create, enable/disable, delete.

import { useState, useEffect, useCallback } from 'react';
import { observer } from '@react';
import { useWebhook, useI18n } from '@react/hooks';
import { webhookApi } from '@core/api';
import {
  Button, Input, Textarea, Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter,
  Alert, Badge, ErrorAlert,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty,
} from '@react/components';
import { Select } from '@/components/Select';
import type { WebhookEndpoint, WebhookAuthType } from '@core/types/webhook';
import type { TranslationKey, TranslationParams } from '@react/hooks';
import {
  getEndpointStatusDisplay, validateCreateForm, formatUrl, formatDateTime,
  eventTypeCount, canDisable, canEnable, canDelete, needsAuthConfig,
  EMPTY_CREATE_FORM,
} from '../../features/webhooks/logic';
import type { CreateEndpointFormState } from '../../features/webhooks/logic';

type TFn = (key: TranslationKey, params?: TranslationParams) => string;

// --- Inline SVG icons ---

function CopyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function GlobeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
    </svg>
  );
}

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function ChevronIcon({ className, open }: { className?: string; open: boolean }) {
  return (
    <svg
      className={`${className ?? ''} transition-transform ${open ? 'rotate-180' : ''}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

// --- Copy helper ---

function useCopyToClipboard() {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const input = document.createElement('textarea');
      input.value = text;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, []);
  return { copied, copy };
}

// --- Secret banner ---

function SecretBanner({ secret, onDismiss, t }: { secret: string; onDismiss: () => void; t: TFn }) {
  const { copied, copy } = useCopyToClipboard();

  return (
    <Alert variant="success" dismissible onDismiss={onDismiss}>
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <ShieldIcon className="h-5 w-5 text-green-600 flex-shrink-0" />
          <p className="text-sm font-medium">{t('webhooks.secretCreatedTitle')}</p>
        </div>
        <p className="text-xs text-muted-foreground">{t('webhooks.secretCreatedWarning')}</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 px-3 py-2 rounded-md bg-muted text-foreground font-mono text-sm break-all select-all">
            {secret}
          </code>
          <Button variant="secondary" size="sm" onClick={() => copy(secret)} aria-label={t('common.copy')}>
            {copied
              ? <CheckIcon className="h-4 w-4 text-green-600" />
              : <CopyIcon className="h-4 w-4" />
            }
          </Button>
        </div>
      </div>
    </Alert>
  );
}

// --- Create form ---

const AUTH_TYPE_OPTIONS = [
  { value: 'WEBHOOK_AUTH_TYPE_HMAC', label: 'HMAC SHA-256' },
  { value: 'WEBHOOK_AUTH_TYPE_BEARER', label: 'Bearer Token' },
  { value: 'WEBHOOK_AUTH_TYPE_BASIC', label: 'Basic Auth' },
  { value: 'WEBHOOK_AUTH_TYPE_HEADER', label: 'Custom Header' },
  { value: 'WEBHOOK_AUTH_TYPE_NONE', label: 'None' },
];

function CreateEndpointForm({ onCreated, onCancel, t }: {
  onCreated: (secret: string) => void;
  onCancel: () => void;
  t: TFn;
}) {
  const [form, setForm] = useState<CreateEndpointFormState>({ ...EMPTY_CREATE_FORM });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(null);

  const handleChange = (field: keyof CreateEndpointFormState, value: string | boolean | string[]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => { const next = { ...prev }; delete next[field]; return next; });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validateCreateForm(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await webhookApi.CreateEndpoint({
        endpoint: {
          name: form.name.trim(),
          description: form.description.trim(),
          url: form.url.trim(),
          auth_type: form.authType as WebhookAuthType,
          auth_header: form.authHeader,
          auth_value: form.authValue,
          subscribe_all: form.subscribeAll,
          event_types: form.subscribeAll ? [] : form.eventTypes,
        } as any,
      });
      onCreated(response.endpoint?.secret ?? '');
    } catch (err) {
      setSubmitError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <form onSubmit={handleSubmit}>
        <CardHeader>
          <CardTitle>{t('webhooks.createEndpoint')}</CardTitle>
          <CardDescription>{t('webhooks.sectionBasicDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {submitError != null && <ErrorAlert error={submitError} dismissible onDismiss={() => setSubmitError(null)} />}

          <Input
            label={t('webhooks.fieldName')}
            value={form.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder={t('webhooks.fieldNamePlaceholder')}
            error={errors.name ? t(errors.name) : undefined}
            required
          />
          <Textarea
            label={t('webhooks.fieldDescription')}
            value={form.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder={t('webhooks.fieldDescriptionPlaceholder')}
          />
          <Input
            label={t('webhooks.fieldUrl')}
            value={form.url}
            onChange={(e) => handleChange('url', e.target.value)}
            placeholder="https://example.com/webhooks"
            error={errors.url ? t(errors.url) : undefined}
            required
          />
          <Select
            label={t('webhooks.fieldAuthType')}
            value={form.authType}
            onChange={(e: any) => handleChange('authType', e.target.value)} options={AUTH_TYPE_OPTIONS} />
          {needsAuthConfig(form.authType) && form.authType === 'WEBHOOK_AUTH_TYPE_HEADER' && (
            <Input
              label={t('webhooks.fieldAuthHeader')}
              value={form.authHeader}
              onChange={(e) => handleChange('authHeader', e.target.value)}
              placeholder="X-Webhook-Token"
            />
          )}
          {needsAuthConfig(form.authType) && form.authType !== 'WEBHOOK_AUTH_TYPE_HMAC' && (
            <Input
              label={t('webhooks.fieldAuthValue')}
              value={form.authValue}
              onChange={(e) => handleChange('authValue', e.target.value)}
              placeholder="your-secret-value"
            />
          )}
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.subscribeAll}
              onChange={(e) => handleChange('subscribeAll', e.target.checked)}
              className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
            />
            <span className="text-sm font-medium text-foreground">{t('webhooks.subscribeAll')}</span>
          </label>
        </CardContent>
        <CardFooter className="flex justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? t('common.creating') : t('webhooks.createEndpoint')}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}

// --- Endpoint row ---

function EndpointRow({ endpoint, onToggle, onDelete, t }: {
  endpoint: WebhookEndpoint;
  onToggle: (id: string, enable: boolean) => void;
  onDelete: (id: string) => void;
  t: TFn;
}) {
  const [expanded, setExpanded] = useState(false);
  const id = endpoint.entity?.id ?? '';
  const status = getEndpointStatusDisplay(endpoint.status);

  return (
    <>
      <TableRow className="cursor-pointer" onClick={() => setExpanded((v) => !v)}>
        <TableCell>
          <div className="flex items-center gap-2">
            <ChevronIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" open={expanded} />
            <div>
              <p className="text-sm font-medium text-foreground">{endpoint.name}</p>
              {endpoint.description && (
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{endpoint.description}</p>
              )}
            </div>
          </div>
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1.5">
            <GlobeIcon className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
            <code className="text-xs text-muted-foreground truncate max-w-[180px]">{formatUrl(endpoint.url)}</code>
          </div>
        </TableCell>
        <TableCell>
          <Badge variant={status.variant}>{t(status.labelKey)}</Badge>
        </TableCell>
        <TableCell className="text-sm text-muted-foreground">
          {eventTypeCount(endpoint)}
        </TableCell>
        <TableCell className="text-right">
          <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            {canDisable(endpoint) && (
              <Button variant="ghost" size="sm" onClick={() => onToggle(id, false)}>
                {t('webhooks.disable')}
              </Button>
            )}
            {canEnable(endpoint) && (
              <Button variant="ghost" size="sm" onClick={() => onToggle(id, true)}>
                {t('webhooks.enable')}
              </Button>
            )}
            {canDelete(endpoint) && (
              <Button variant="ghost" size="sm" className="text-destructive" onClick={() => onDelete(id)}>
                {t('common.delete')}
              </Button>
            )}
          </div>
        </TableCell>
      </TableRow>

      {expanded && (
        <TableRow>
          <TableCell colSpan={5} className="bg-muted/20 px-6 py-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-xs">
              <div>
                <p className="font-medium text-muted-foreground mb-1">{t('webhooks.fieldUrl')}</p>
                <code className="text-foreground break-all">{endpoint.url}</code>
              </div>
              <div>
                <p className="font-medium text-muted-foreground mb-1">{t('webhooks.fieldAuthType')}</p>
                <p className="text-foreground">{endpoint.auth_type?.replace('WEBHOOK_AUTH_TYPE_', '') ?? 'HMAC'}</p>
              </div>
              <div>
                <p className="font-medium text-muted-foreground mb-1">{t('webhooks.fieldFailures')}</p>
                <p className={endpoint.consecutive_failures > 0 ? 'text-destructive font-medium' : 'text-foreground'}>
                  {endpoint.consecutive_failures}
                </p>
              </div>
              <div>
                <p className="font-medium text-muted-foreground mb-1">{t('webhooks.fieldLastSuccess')}</p>
                <p className="text-foreground">{formatDateTime(endpoint.last_success_at as any)}</p>
              </div>
              <div>
                <p className="font-medium text-muted-foreground mb-1">{t('webhooks.fieldCreated')}</p>
                <p className="text-foreground">{formatDateTime(endpoint.created_at as any)}</p>
              </div>
              {endpoint.subscribe_all ? (
                <div>
                  <p className="font-medium text-muted-foreground mb-1">{t('webhooks.fieldEvents')}</p>
                  <Badge variant="default">{t('webhooks.allEvents')}</Badge>
                </div>
              ) : endpoint.event_types?.length > 0 ? (
                <div className="col-span-2 md:col-span-3">
                  <p className="font-medium text-muted-foreground mb-1">{t('webhooks.fieldEvents')}</p>
                  <div className="flex flex-wrap gap-1">
                    {endpoint.event_types.map((et) => (
                      <Badge key={et} variant="secondary">{et}</Badge>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// --- Main page ---

export const WebhooksPage = observer(() => {
  const store = useWebhook();
  const { t } = useI18n();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);
  const [endpoints, setEndpoints] = useState<WebhookEndpoint[]>([]);

  useEffect(() => {
    loadEndpoints();
  }, []);

  const loadEndpoints = async () => {
    try {
      const response = await store.ListEndpoints();
      setEndpoints(response.endpoints?.items ?? []);
    } catch {
      // Error handled by store
    }
  };

  const handleCreated = (secret: string) => {
    setCreatedSecret(secret);
    setShowCreateForm(false);
    loadEndpoints();
  };

  const handleToggle = async (id: string, enable: boolean) => {
    try {
      if (enable) {
        await store.EnableEndpoint(id, {});
      } else {
        await store.DisableEndpoint(id, {});
      }
      loadEndpoints();
    } catch {
      // Error handled by store
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('webhooks.deleteConfirm'))) return;
    try {
      await store.DeleteEndpoint(id);
      loadEndpoints();
    } catch {
      // Error handled by store
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{t('webhooks.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('webhooks.settingsSubtitle')}</p>
        </div>
        {!showCreateForm && (
          <Button onClick={() => { setShowCreateForm(true); setCreatedSecret(null); }}>
            <PlusIcon className="h-4 w-4 mr-2" />
            {t('webhooks.createEndpoint')}
          </Button>
        )}
      </div>

      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      {/* Secret banner */}
      {createdSecret && (
        <SecretBanner secret={createdSecret} onDismiss={() => setCreatedSecret(null)} t={t} />
      )}

      {/* Create form */}
      {showCreateForm && (
        <CreateEndpointForm
          onCreated={handleCreated}
          onCancel={() => setShowCreateForm(false)}
          t={t}
        />
      )}

      {/* Security hint */}
      <div className="flex items-start gap-3 px-4 py-3 rounded-lg bg-muted/50 border border-border">
        <GlobeIcon className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
        <p className="text-sm text-muted-foreground">{t('webhooks.securityHint')}</p>
      </div>

      {/* Endpoints table */}
      <Card>
        <Table hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('webhooks.columnName')}</TableHead>
              <TableHead>{t('webhooks.columnUrl')}</TableHead>
              <TableHead>{t('webhooks.columnStatus')}</TableHead>
              <TableHead>{t('webhooks.columnEvents')}</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12">
                  <div className="text-muted-foreground">{t('common.loading')}</div>
                </TableCell>
              </TableRow>
            ) : endpoints.length === 0 ? (
              <TableEmpty colSpan={5} message={t('webhooks.emptyState')} />
            ) : (
              endpoints.map((ep) => (
                <EndpointRow
                  key={ep.entity?.id}
                  endpoint={ep}
                  onToggle={handleToggle}
                  onDelete={handleDelete}
                  t={t}
                />
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default WebhooksPage;
